import type { ReactNode } from 'react';
import { formatMoney } from '../../mirror/time';
import type { T, UiLocale } from '../../messages';
import type { PreviewLine, WaterfallPreview } from '../../money/waterfall';
import type { LineDraft } from './model';

/**
 * **The sale's lines** — name, length, a − qty + stepper, the line's price and a
 * way to take it off.
 *
 * A priced item's price is the catalog's and there is no box to type over it:
 * a discount is how she gives something away, and it is one quiet link under
 * the subtotal. The only price a cashier types is an **open-price** line's,
 * and tapping that price reopens the sheet it was typed in. A pickup order's
 * lines carry the price the guest was **quoted**; when that differs from the
 * list, the list stays on screen, struck through, because the gap is the
 * discount the report will show (§7.4 rule 3).
 */
export function CartLines({
  lines,
  previewLines,
  currency,
  locale,
  t,
  onQty,
  onRemove,
  onEditPrice,
}: {
  lines: readonly LineDraft[];
  previewLines: readonly PreviewLine[];
  currency: string;
  locale: UiLocale;
  t: T;
  onQty: (key: string, qty: number) => void;
  onRemove: (key: string) => void;
  onEditPrice: (key: string) => void;
}): ReactNode {
  const money = (cents: number): string => formatMoney(cents, currency, locale);
  if (lines.length === 0) {
    return <p className="pos-cart__empty muted">{t('checkout.cartEmpty')}</p>;
  }
  return (
    <ul className="pos-lines" aria-label={t('checkout.cartLabel')}>
      {lines.map((line, index) => {
        const preview = previewLines[index];
        const list = preview?.listUnitCents ?? 0;
        const charged = preview?.chargedUnitCents ?? 0;
        const parent =
          line.addOnOf === undefined
            ? undefined
            : lines.find((l) => l.itemId === line.addOnOf && l.addOnOf === undefined);
        const details = [
          line.lengthLabel,
          parent === undefined ? undefined : t('checkout.addOnOf', { name: parent.name }),
          line.note.trim() === '' ? undefined : line.note.trim(),
        ].filter((d): d is string => d !== undefined && d !== '');
        return (
          <li key={line.key} className="pos-line">
            <div className="pos-line__what">
              <strong>{line.name}</strong>
              {details.length > 0 ? <small>{details.join(' · ')}</small> : null}
              {!line.openPrice && charged !== list ? (
                <small>
                  <s>{money(list)}</s>
                  {t('checkout.listAndOff', { off: money(list - charged) })}
                </small>
              ) : null}
            </div>
            <div className="pos-stepper">
              <button
                type="button"
                className="pos-stepper__button"
                aria-label={t('checkout.lessQty', { name: line.name })}
                disabled={line.qty <= 1}
                onClick={() => onQty(line.key, line.qty - 1)}
              >
                −
              </button>
              <output
                className="pos-stepper__qty"
                aria-label={t('checkout.qtyLabel', { name: line.name })}
              >
                {line.qty}
              </output>
              <button
                type="button"
                className="pos-stepper__button"
                aria-label={t('checkout.moreQty', { name: line.name })}
                onClick={() => onQty(line.key, line.qty + 1)}
              >
                +
              </button>
            </div>
            {line.openPrice ? (
              <button
                type="button"
                className="pos-line__price pos-line__price--open"
                aria-label={t('checkout.priceLabel', { name: line.name })}
                onClick={() => onEditPrice(line.key)}
              >
                {money(charged * line.qty)}
              </button>
            ) : (
              <span className="pos-line__price">{money(charged * line.qty)}</span>
            )}
            <button
              type="button"
              className="pos-line__remove"
              aria-label={t('checkout.removeLine', { name: line.name })}
              onClick={() => onRemove(line.key)}
            >
              ×
            </button>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * **Subtotal, discount, tax, total** — and, under it, what each instrument
 * took and what is left to pay now.
 *
 * Discount and tax are quiet links that open a field only when used; a tax
 * already typed keeps its field open. The numbers are the preview's
 * (`money/waterfall.ts`, a transcription of the server's own function) and the
 * request never carries any of them — the server prices the document.
 */
export function Totals({
  preview,
  listSubtotalCents,
  currency,
  locale,
  t,
  discount,
  tax,
}: {
  preview: WaterfallPreview;
  /** `Σ list × qty`, for when the preview has refused and has no numbers. */
  listSubtotalCents: number;
  currency: string;
  locale: UiLocale;
  t: T;
  discount: AmountField;
  tax: AmountField;
}): ReactNode {
  const money = (cents: number): string => formatMoney(cents, currency, locale);
  const ok = preview.ok ? preview : null;
  const applied = ok?.applied;

  return (
    <div className="pos-totals">
      <Row
        label={t('checkout.subtotalLabel')}
        value={money(ok?.subtotalCents ?? listSubtotalCents)}
      />

      {ok !== null && ok.discountCents > 0 ? (
        <Row label={t('checkout.discountLabel')} value={`−${money(ok.discountCents)}`} />
      ) : null}
      {discount.open ? <OpenAmount field={discount} t={t} /> : null}

      {ok !== null && ok.taxCents > 0 ? (
        <Row label={t('checkout.tax')} value={`+${money(ok.taxCents)}`} />
      ) : null}
      {tax.open ? <OpenAmount field={tax} t={t} /> : null}

      {/* The two quiet links share one row until one of them is used. */}
      {discount.open && tax.open ? null : (
        <div className="pos-totals__adds">
          {[discount, tax]
            .filter((field) => !field.open)
            .map((field) => (
              <button
                key={field.addLabel}
                type="button"
                className="link-button"
                onClick={field.onOpen}
              >
                {field.addLabel}
              </button>
            ))}
        </div>
      )}

      <div className="pos-totals__total">
        <span>{t('checkout.totalLabel')}</span>
        <strong>{ok === null ? '—' : money(ok.totalCents)}</strong>
      </div>

      {applied !== undefined ? (
        <>
          {applied.certificateCents > 0 ? (
            <Row label={t('checkout.rowGiftCard')} value={`−${money(applied.certificateCents)}`} />
          ) : null}
          {applied.depositCents > 0 ? (
            <Row label={t('checkout.rowDeposit')} value={`−${money(applied.depositCents)}`} />
          ) : null}
          {applied.cashCreditCents > 0 ? (
            <Row label={t('checkout.rowCredit')} value={`−${money(applied.cashCreditCents)}`} />
          ) : null}
          {ok !== null && ok.tipCents > 0 ? (
            <Row label={t('checkout.tip')} value={`+${money(ok.tipCents)}`} />
          ) : null}
          {applied.certificateCents + applied.depositCents + applied.cashCreditCents > 0 ||
          (ok !== null && ok.tipCents > 0) ? (
            <Row label={t('checkout.rowToPay')} value={money(applied.remainderCents)} strong />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/** A field behind a quiet link: "Add discount" until it is used. */
export interface AmountField {
  open: boolean;
  text: string;
  label: string;
  addLabel: string;
  removeLabel: string;
  onOpen: () => void;
  onChange: (text: string) => void;
  onRemove: () => void;
}

function OpenAmount({ field, t }: { field: AmountField; t: T }): ReactNode {
  return (
    <div className="pos-totals__field">
      <span aria-hidden="true">{field.label}</span>
      <input
        aria-label={field.label}
        inputMode="decimal"
        placeholder="0.00"
        value={field.text}
        onChange={(e) => field.onChange(e.target.value)}
      />
      <button
        type="button"
        className="link-button link-button--danger"
        aria-label={field.removeLabel}
        onClick={field.onRemove}
      >
        {t('common.remove')}
      </button>
    </div>
  );
}

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}): ReactNode {
  return (
    <div className={`pos-totals__row${strong === true ? ' is-strong' : ''}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
